export type PositionLookup = [start: number, end: number];
export type StructureDesc = RegExp;

export interface IbanCountryMeta {
  countryName: string;
  /**
   * Set this to a static string to disable
   * Module-97 check digit validation and
   * validate this string instead
   */
  checkDigits?: string;
  bban: {
    structure: StructureDesc;
    // TODO: Make this a regex to extract bank ID
    bankIdPosition: PositionLookup;
    // TODO: Make this a regex to extract branch ID
    branchIdPosition?: PositionLookup;
  };
}

export interface IbanRegistry {
  [k: string]: IbanCountryMeta;
}

/**
 * A registry that lists the countries that support
 * IBAN and that contains data about the domestic structure
 * of the BBAN.
 *
 * @see https://www.swift.com/sites/default/files/resources/iban_registry.pdf
 */
export const registry: IbanRegistry = {
  AD: {
    countryName: 'Andorra',
    bban: {
      // BBAN structure: 4!n4!n12!c
      structure: /^\d{4}\d{4}[\da-zA-Z]{12}$/,
      bankIdPosition: [0, 3],
      branchIdPosition: [4, 7],
    },
  },
  AE: {
    countryName: 'United Arab Emirates',
    bban: {
      // BBAN structure: 3!n16!n
      structure: /^\d{3}\d{16}$/,
      bankIdPosition: [0, 2],
    },
  },
  AL: {
    countryName: 'Albania',
    bban: {
      // BBAN structure: 8!n16!c
      structure: /^\d{8}[\da-zA-Z]{16}$/,
      bankIdPosition: [0, 2],
      branchIdPosition: [3, 6],
    },
  },
  AT: {
    countryName: 'Austria',
    bban: {
      // BBAN structure: 5!n11!n
      structure: /^\d{5}\d{11}$/,
      bankIdPosition: [0, 4],
    },
  },
  AZ: {
    countryName: 'Republic of Azerbaijan',
    bban: {
      // BBAN structure: 4!a20!c
      structure: /^[A-Z]{4}[\da-zA-Z]{20}$/,
      bankIdPosition: [0, 3],
    },
  },
  BA: {
    countryName: 'Bosnia and Herzegovina',
    bban: {
      // BBAN structure: 3!n3!n8!n2!n
      structure: /^\d{3}\d{3}\d{8}\d{2}$/,
      bankIdPosition: [0, 2],
      branchIdPosition: [3, 5],
    },
  },
  BE: {
    countryName: 'Belgium',
    bban: {
      // BBAN structure: 3!n7!n2!n
      structure: /^\d{3}\d{7}\d{2}$/,
      bankIdPosition: [0, 2],
    },
  },
  BG: {
    countryName: 'Bulgaria',
    bban: {
      // BBAN structure: 4!a4!n2!n8!c
      structure: /^[A-Z]{4}\d{4}\d{2}[\da-zA-Z]{8}$/,
      bankIdPosition: [0, 3],
      branchIdPosition: [4, 7],
    },
  },
  BH: {
    countryName: 'Kingdom of Bahrain',
    bban: {
      // BBAN structure: 4!a14!c
      structure: /^[A-Z]{4}[\da-zA-Z]{14}$/,
      bankIdPosition: [0, 3],
    },
  },
  BR: {
    countryName: 'Brazil',
    bban: {
      // BBAN structure: 8!n5!n10!n1!a1!c
      structure: /^\d{8}\d{5}\d{10}[A-Z]{1}[\da-zA-Z]{1}$/,
      bankIdPosition: [0, 7],
      branchIdPosition: [8, 12],
    },
  },
  CR: {
    countryName: 'Costa Rica',
    bban: {
      // BBAN structure: 3!n14!n
      structure: /^\d{3}\d{14}$/,
      bankIdPosition: [0, 2],
    },
  },
  CH: {
    countryName: 'Switzerland',
    bban: {
      // BBAN structure: 5!n12!c
      structure: /^\d{5}[\da-zA-Z]{12}$/,
      bankIdPosition: [0, 4],
    },
  },
  CY: {
    countryName: 'Cyprus',
    bban: {
      // BBAN structure: 3!n5!n16!c
      structure: /^\d{3}\d{5}[\da-zA-Z]{16}$/,
      bankIdPosition: [0, 2],
      branchIdPosition: [3, 7],
    },
  },
  CZ: {
    countryName: 'Czech Republic',
    bban: {
      // BBAN structure: 4!n6!n10!n
      structure: /^\d{4}\d{6}\d{10}$/,
      bankIdPosition: [0, 3],
    },
  },
  DE: {
    countryName: 'Germany',
    bban: {
      // BBAN structure: 8!n10!n
      structure: /^\d{8}\d{10}$/,
      bankIdPosition: [0, 7],
    },
  },
  DK: {
    countryName: 'Denmark',
    bban: {
      // BBAN structure: 4!n9!n1!n
      structure: /^\d{4}\d{9}\d{1}$/,
      bankIdPosition: [0, 3],
    },
  },
  DO: {
    countryName: 'Dominican Republic',
    bban: {
      // BBAN structure: 4!c20!n
      structure: /^[[\da-zA-Z]{4}\d{20}$/,
      bankIdPosition: [0, 3],
    },
  },
  EE: {
    countryName: 'Estonia',
    bban: {
      // BBAN structure: 2!n2!n11!n1!n
      structure: /^[1-9]\d\d{2}\d{11}\d{1}$/,
      bankIdPosition: [0, 1],
    },
  },
  ES: {
    countryName: 'Spain',
    bban: {
      // BBAN structure: 4!n4!n1!n1!n10!n
      structure: /^\d{4}\d{4}\d\d\d{10}$/,
      bankIdPosition: [0, 3],
      branchIdPosition: [4, 7],
    },
  },
  FI: {
    countryName: 'Finland',
    bban: {
      // TODO: This is only an approximation, because actually the
      // Bank ID position varies.
      // BBAN structure: 3!n11!n
      structure: /^\d{3}\d{11}$/,
      // TODO: Change to regex once this is supported here
      // https://www.finanssiala.fi/en/topics/payment-services-in-finland/payment-technical-documents/
      bankIdPosition: [0, 2],
    },
  },
  FR: {
    countryName: 'France',
    bban: {
      // BBAN structure: 5!n5!n11!c2!n
      structure: /^\d{5}\d{5}[\da-zA-Z]{11}\d{2}$/,
      bankIdPosition: [0, 4],
    },
  },
  GB: {
    countryName: 'United Kingdom',
    bban: {
      // BBAN structure: 4!a6!n8!n
      structure: /^[A-Z]{4}\d{6}\d{8}$/,
      bankIdPosition: [0, 3],
      branchIdPosition: [4, 9],
    },
  },
  GE: {
    countryName: 'Georgia',
    bban: {
      // BBAN structure: 2!a16!n
      structure: /^[A-Z]{2}\d{16}$/,
      bankIdPosition: [0, 1],
    },
  },
  GI: {
    countryName: 'Gibraltar',
    bban: {
      // BBAN structure: 4!a15!c
      structure: /^[A-Z]{4}[\da-zA-Z]{15}$/,
      bankIdPosition: [0, 3],
    },
  },
  GR: {
    countryName: 'Greece',
    bban: {
      // BBAN structure: 3!n4!n16!c
      structure: /^\d{3}\d{4}[\da-zA-Z]{16}$/,
      bankIdPosition: [0, 2],
      branchIdPosition: [3, 6],
    },
  },
  GT: {
    countryName: 'Guatemala',
    bban: {
      // BBAN structure: 4!c20!c
      structure: /^[\da-zA-Z]{4}[\da-zA-Z]{20}$/,
      bankIdPosition: [0, 3],
    },
  },
  HR: {
    countryName: 'Croatia',
    bban: {
      // BBAN structure: 7!n10!n
      structure: /^\d{7}\d{10}$/,
      bankIdPosition: [0, 6],
    },
  },
  HU: {
    countryName: 'Hungary',
    bban: {
      // BBAN structure: 3!n4!n1!n15!n1!n
      structure: /^\d{3}\d{4}\d{1}\d{15}\d$/,
      bankIdPosition: [0, 2],
      branchIdPosition: [3, 6],
    },
  },
  IE: {
    countryName: 'Ireland',
    bban: {
      // BBAN structure: 4!a6!n8!n
      structure: /^[A-Z]{4}\d{6}\d{8}$/,
      bankIdPosition: [0, 3],
      branchIdPosition: [4, 9],
    },
  },
  IL: {
    countryName: 'Israel',
    bban: {
      // BBAN structure: 3!n3!n13!n
      structure: /^\d{3}\d{3}\d{13}$/,
      bankIdPosition: [0, 2],
      branchIdPosition: [3, 5],
    },
  },
  IS: {
    countryName: 'Iceland',
    bban: {
      // BBAN structure: 4!n2!n6!n10!n
      structure: /^\d{4}\d{2}\d{6}\d{10}$/,
      bankIdPosition: [0, 3],
    },
  },
  IT: {
    countryName: 'Italy',
    bban: {
      // BBAN structure: 1!a5!n5!n12!c
      structure: /^[A-Z]\d{5}\d{5}[\da-zA-Z]{12}$/,
      bankIdPosition: [1, 5],
      branchIdPosition: [6, 10],
    },
  },
  JO: {
    countryName: 'Jordan',
    bban: {
      // BBAN structure: 4!a4!n18!c
      structure: /^[A-Z]{4}\d{4}[\da-zA-Z]{18}$/,
      bankIdPosition: [0, 3],
    },
  },
  KW: {
    countryName: 'Kuwait',
    bban: {
      // BBAN structure: 4!a22!c
      structure: /^[A-Z]{4}[\da-zA-Z]{22}$/,
      bankIdPosition: [0, 3],
    },
  },
  KZ: {
    countryName: 'Kazakhstan',
    bban: {
      // BBAN structure
      structure: /^\d{3}[\da-zA-Z]{13}$/,
      bankIdPosition: [0, 2],
    },
  },
  LB: {
    countryName: 'Lebanon',
    bban: {
      // BBAN structure: 4!n20!c
      structure: /^\d{4}[\da-zA-Z]{20}$/,
      bankIdPosition: [0, 3],
    },
  },
  LC: {
    countryName: 'Saint Lucia',
    bban: {
      // BBAN structure: 4!a24n
      structure: /^[A-Z]{4}\d{24}$/,
      bankIdPosition: [0, 3],
    },
  },
  LI: {
    countryName: 'Liechtenstein (Principality of)',
    bban: {
      // BBAN structure: 5!n12!c
      structure: /^\d{5}[\da-zA-Z]{12}$/,
      bankIdPosition: [0, 4],
    },
  },
  LT: {
    countryName: 'Lithuania',
    bban: {
      // BBAN structure: 5!n11!n
      structure: /^\d{5}\d{11}$/,
      bankIdPosition: [0, 4],
    },
  },
  LU: {
    countryName: 'Luxembourg',
    bban: {
      // BBAN structure: 3!n13!c
      structure: /^\d{3}[\da-zA-Z]{13}$/,
      bankIdPosition: [0, 2],
    },
  },
  LV: {
    countryName: 'Latvia',
    bban: {
      // BBAN structure: 4!a13!c
      structure: /^[A-Z]{4}[\da-zA-Z]{13}$/,
      bankIdPosition: [0, 3],
    },
  },
  MC: {
    countryName: 'Monaco',
    bban: {
      // BBAN structure: 5!n5!n11!c2!n
      structure: /^\d{5}\d{5}[\da-zA-Z]{11}\d{2}$/,
      bankIdPosition: [0, 4],
      branchIdPosition: [5, 9],
    },
  },
  MD: {
    countryName: 'Moldova',
    bban: {
      // BBAN structure: 2!c!18!c
      structure: /^[\da-zA-Z]{2}[\da-zA-Z]{18}$/,
      bankIdPosition: [0, 1],
    },
  },
  ME: {
    countryName: 'Montenegro',
    bban: {
      // BBAN structure: 3!n13!n2!n
      structure: /^\d{3}\d{13}\d{2}$/,
      bankIdPosition: [0, 2],
    },
  },
  MK: {
    countryName: 'Macedonia',
    bban: {
      // BBAN structure: 3!n10!c2!n
      structure: /^\d{3}[\da-zA-Z]{10}\d{2}$/,
      bankIdPosition: [0, 2],
    },
  },
  MR: {
    countryName: 'Mauritania',
    bban: {
      // BBAN structure: 5!n5!n11!n2!n
      structure: /^\d{5}\d{5}\d{11}\d{2}$/,
      bankIdPosition: [0, 4],
      branchIdPosition: [5, 9],
    },
  },
  MU: {
    countryName: 'Mauritius',
    bban: {
      // BBAN structure: 4!a2!n2!n12!n3!n3!a
      structure: /^[A-Z]{4}\d{2}\d{2}\d{12}\d{3}[A-Z]{3}$/,
      bankIdPosition: [0, 5],
      branchIdPosition: [6, 7],
    },
  },
  MT: {
    countryName: 'Malta',
    bban: {
      // BBAN structure: 4!a5!n18!c
      structure: /^[A-Z]{4}\d{5}[\da-zA-Z]{18}$/,
      bankIdPosition: [0, 3],
      branchIdPosition: [4, 8],
    },
  },
  NL: {
    countryName: 'The Netherlands',
    bban: {
      // BBAN structure: 4!a10!n
      structure: /^[A-Z]{4}\d{10}$/,
      bankIdPosition: [0, 3],
    },
  },
  NO: {
    countryName: 'Norway',
    bban: {
      // BBAN structure: 4!n6!n1!n
      structure: /^\d{4}\d{6}\d{1}$/,
      bankIdPosition: [0, 3],
    },
  },
  PK: {
    countryName: 'Pakistan',
    bban: {
      // BBAN structure: 4!a16!c
      structure: /^[A-Z]{4}[\da-zA-Z]{16}$/,
      bankIdPosition: [0, 3],
    },
  },
  PL: {
    countryName: 'Poland',
    bban: {
      // BBAN structure: 8!n16!n
      structure: /^\d{8}\d{16}$/,
      bankIdPosition: [0, 7],
    },
  },
  PS: {
    countryName: 'Palestine, State of',
    bban: {
      // BBAN structure: 4!a21!c
      structure: /^[A-Z]{4}[\da-zA-Z]{21}$/,
      bankIdPosition: [0, 3],
    },
  },
  PT: {
    countryName: 'Portugal',
    bban: {
      // BBAN structure: 4!n4!n11!n2!n
      structure: /^\d{4}\d{4}\d{11}\d{2}$/,
      bankIdPosition: [0, 3],
    },
  },
  QA: {
    countryName: 'Qatar',
    bban: {
      // BBAN structure: 4!a21!c
      structure: /^[A-Z]{4}[\da-zA-Z]{21}$/,
      bankIdPosition: [0, 3],
    },
  },
  RO: {
    countryName: 'Romania',
    bban: {
      // BBAN structure: 4!a16!c
      structure: /^[A-Z]{4}[\da-zA-Z]{16}$/,
      bankIdPosition: [0, 3],
    },
  },
  RS: {
    countryName: 'Serbia',
    bban: {
      // BBAN structure: 3!n13!n2!n
      structure: /^\d{3}\d{13}\d{2}$/,
      bankIdPosition: [0, 2],
    },
  },
  SA: {
    countryName: 'Saudi Arabia',
    bban: {
      // BBAN structure: 2!n18!c
      structure: /^\d{2}[\da-zA-Z]{18}$/,
      bankIdPosition: [0, 1],
    },
  },
  SE: {
    countryName: 'Sweden',
    bban: {
      // BBAN structure: 3!n16!n1!n
      structure: /^\d{3}\d{16}\d{1}$/,
      bankIdPosition: [0, 2],
    },
  },
  SI: {
    countryName: 'Slovenia',
    bban: {
      // BBAN structure: 5!n8!n2!n
      structure: /^\d{5}\d{8}\d{2}$/,
      // Positions 1-5(positions 1-2 bank identifier; positions 3-5 branch identifier)
      // In case of payment institutions:
      // positions 1-5 payment institution identifier, where positions 1-2 is always 91
      bankIdPosition: [0, 4],
      branchIdPosition: [2, 4],
    },
  },
  SK: {
    countryName: 'Slovak Republic',
    bban: {
      // BBAN structure: 4!n6!n10!n
      structure: /^\d{4}\d{6}\d{10}$/,
      bankIdPosition: [0, 3],
    },
  },
  SM: {
    countryName: 'San Marino',
    bban: {
      // BBAN structure: 1!a5!n5!n12!c
      structure: /^[A-Z]{1}\d{5}\d{5}[\da-zA-Z]{12}$/,
      bankIdPosition: [1, 5],
      branchIdPosition: [6, 10],
    },
  },
  ST: {
    countryName: 'Sao Tome And Principe',
    bban: {
      // BBAN structure: 8!n11!n2!n
      structure: /^\d{8}\d{11}\d{2}$/,
      bankIdPosition: [0, 3],
      branchIdPosition: [4, 7],
    },
  },
  TL: {
    countryName: 'Timor-Leste',
    // Check digits seems to always be 38
    checkDigits: '38',
    /**
     * IBAN: TL2!n3!n14!n2!n
     * Positions 1-4: TL38
     * Positions 5-7: Bank Code
     * Positions 8-21: Customer account number
     * Positions22-23: National check digit (modulo 97)
     */
    bban: {
      // BBAN structure: 3!n 14!n 2!n
      structure: /^\d{3}\d{14}\d{2}$/,
      bankIdPosition: [0, 2],
    },
  },
  TN: {
    countryName: 'Tunisia',
    bban: {
      // BBAN structure: 2!n3!n13!n2!n
      structure: /^\d{2}\d{3}\d{13}\d{2}$/,
      bankIdPosition: [0, 1],
      branchIdPosition: [2, 4],
    },
  },
  TR: {
    countryName: 'Turkey',
    bban: {
      // BBAN structure: 5!n1!n16!c
      structure: /^\d{5}\d{1}[\da-zA-Z]{16}$/,
      bankIdPosition: [0, 4],
    },
  },
  VG: {
    countryName: 'Virgin Islands, British',
    bban: {
      // BBAN structure: 4!a16!n
      structure: /^[A-Z]{4}\d{16}$/,
      bankIdPosition: [0, 3],
    },
  },
  XK: {
    countryName: 'Republic of Kosovo',
    bban: {
      // BBAN structure: 4!n10!n2!n
      structure: /^\d{4}\d{10}\d{2}$/,
      bankIdPosition: [0, 1],
      branchIdPosition: [2, 3],
    },
  },
};

// Add countries "belonging" to denmark
registry['FO'] = {
  ...registry['DK'],
  countryName: 'Faroe Islands',
};
registry['GL'] = {
  ...registry['DK'],
  countryName: 'Greenland',
};

// Add countries "belonging" to Finland
registry['AX'] = {
  ...registry['FI'],
  countryName: 'Åland Islands',
};

// Add countries "belonging" to france
registry['GF'] = {
  ...registry['FR'],
  countryName: 'French Guyana',
};
registry['GP'] = {
  ...registry['FR'],
  countryName: 'Guadeloupe',
};
registry['MQ'] = {
  ...registry['FR'],
  countryName: 'Martinique',
};
registry['RE'] = {
  ...registry['FR'],
  countryName: 'Reunion',
};
registry['PF'] = {
  ...registry['FR'],
  countryName: 'French Polynesia',
};
registry['TF'] = {
  ...registry['FR'],
  countryName: 'French Southern Territories',
};
registry['YT'] = {
  ...registry['FR'],
  countryName: 'Mayotte',
};
registry['NC'] = {
  ...registry['FR'],
  countryName: 'New Caledonia',
};
registry['BL'] = {
  ...registry['FR'],
  countryName: 'Saint Barthelemy',
};
registry['MF'] = {
  ...registry['FR'],
  countryName: 'Saint Martin (French part)',
};
registry['PM'] = {
  ...registry['FR'],
  countryName: 'Saint Pierre et Miquelon',
};
registry['WF'] = {
  ...registry['FR'],
  countryName: 'Wallis and Futuna Islands',
};
